import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronRight, 
  faTree, 
  faSearch,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { ECRCode } from '../../types/ecrCode';

interface ECRCodeSelectorProps {
  codes: ECRCode[];
  selectedCode?: string;
  onSelect: (code: ECRCode) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const ECRCodeSelector: React.FC<ECRCodeSelectorProps> = ({
  codes,
  selectedCode,
  onSelect,
  placeholder = "ECR-Code auswählen...",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCodeData, setSelectedCodeData] = useState<ECRCode | null>(null);

  // Finde den ausgewählten Code in der Hierarchie
  useEffect(() => {
    if (selectedCode && codes && Array.isArray(codes)) {
      const findCode = (codeList: ECRCode[]): ECRCode | null => {
        if (!codeList || !Array.isArray(codeList)) return null;
        
        for (const code of codeList) {
          if (code.fullCode === selectedCode || code.value === selectedCode) {
            return code;
          }
          if (code.children && Array.isArray(code.children)) {
            const found = findCode(code.children);
            if (found) return found;
          }
        }
        return null;
      };
      setSelectedCodeData(findCode(codes));
    } else {
      setSelectedCodeData(null);
    }
  }, [selectedCode, codes]);

  const toggleExpand = (codeValue: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(codeValue)) {
      newExpanded.delete(codeValue);
    } else {
      newExpanded.add(codeValue);
    }
    setExpandedNodes(newExpanded);
  };

  const handleSelect = (code: ECRCode) => {
    // Nur die unterste Ebene kann ausgewählt werden
    if (!code.children || code.children.length === 0) {
      onSelect(code);
      setIsOpen(false);
    } else {
      toggleExpand(code.value);
    }
  };

  // Filtere Codes basierend auf Suchterm
  const filterCodes = (codeList: ECRCode[], term: string): ECRCode[] => {
    if (!codeList || !Array.isArray(codeList)) return [];
    if (!term) return codeList;

    const filtered: ECRCode[] = [];
    
    for (const code of codeList) {
      const matches = 
        code.value.toLowerCase().includes(term.toLowerCase()) ||
        code.description.toLowerCase().includes(term.toLowerCase());

      if (matches) {
        filtered.push({
          ...code,
          children: code.children && Array.isArray(code.children) ? filterCodes(code.children, term) : undefined
        });
      } else if (code.children && Array.isArray(code.children)) {
        const filteredChildren = filterCodes(code.children, term);
        if (filteredChildren.length > 0) {
          filtered.push({
            ...code,
            children: filteredChildren
          });
          // Automatisch expandieren wenn Kinder gefiltert sind
          expandedNodes.add(code.value);
        }
      }
    }

    return filtered;
  };

  const renderCodeTree = (codeList: ECRCode[], level = 0) => {
    if (!codeList || !Array.isArray(codeList)) return null;
    
    const filteredCodes = filterCodes(codeList, searchTerm);

    return filteredCodes.map(code => {
      const hasChildren = code.children && Array.isArray(code.children) && code.children.length > 0;
      const isExpanded = expandedNodes.has(code.value);
      const canSelect = !hasChildren;

      return (
        <div key={code.value} className={`ml-${level * 3}`}>
          <div
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
              canSelect ? 'hover:bg-blue-50' : ''
            } ${selectedCode === code.fullCode ? 'bg-blue-100 text-blue-900' : ''}`}
            onClick={() => handleSelect(code)}
          >
            {hasChildren && (
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="text-xs text-gray-400 w-3"
              />
            )}
            {!hasChildren && <div className="w-3" />}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium text-gray-700">
                  {code.value}
                </span>
                <span className={`text-sm truncate ${
                  canSelect ? 'text-gray-900' : 'text-gray-600'
                }`}>
                  {code.description}
                </span>
              </div>
              {level === 0 && hasChildren && (
                <div className="text-xs text-gray-500 mt-1">
                  {code.children?.length} Unterkategorien
                </div>
              )}
            </div>

            {canSelect && (
              <div className="text-xs text-green-600 font-medium">
                Auswählbar
              </div>
            )}
          </div>

          {hasChildren && isExpanded && code.children && (
            <div className="border-l border-gray-200 ml-4">
              {renderCodeTree(code.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed ${
          isOpen ? 'ring-1 ring-purple-500 border-transparent' : ''
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FontAwesomeIcon icon={faTree} className="text-gray-400 text-sm" />
          {selectedCodeData ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-sm font-medium text-gray-700">
                {selectedCodeData.fullCode}
              </span>
              <span className="text-sm text-gray-900 truncate">
                {selectedCodeData.description}
              </span>
            </div>
          ) : (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          )}
        </div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`text-gray-400 text-sm transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-2.5 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="ECR-Code oder Bezeichnung suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              )}
            </div>
          </div>

          {/* Code Tree */}
          <div className="max-h-80 overflow-auto">
            {codes && Array.isArray(codes) && codes.length > 0 ? (
              <div className="py-1">
                {renderCodeTree(codes)}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">
                Keine ECR-Codes verfügbar
              </div>
            )}
          </div>

          {/* Info Footer */}
          <div className="p-2 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-600">
              💡 Nur die unterste Ebene der Hierarchie kann ausgewählt werden
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ECRCodeSelector;
